// An organisation's add-ons, as head office attaches and changes them (F-BOOT-17, spec/14 §4.5): Clavian Boot, priced
// per PC a month. It sits on the organisation's page for a role holding `hq:subscriptions`.
//
// Each active add-on shows its PC count, the price per PC it was attached at, and its monthly charge; the active ones'
// total is beneath. Attaching, changing the PC count and cancelling each open in a centred dialog. The monthly charge a
// new count would make is previewed with the same sum the server charges (`addonMonthlyCharge`, integer minor units),
// so the figure in the dialog is the figure that is saved.

import type { Addon, OrganizationAddon } from '@clavian/api-client';
import { addonMonthlyCharge } from '@clavian/domain/addons';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Badge, Button, Dialog, InfoTip, TextField, useToast } from '@clavian/ui-web';
import { useState, type FormEvent, type ReactNode } from 'react';

import { problemDetail, problemStatus } from '../../api/problems';
import { Loading } from '../../shell/layouts';
import { LoadFailure } from '../../shell/LoadFailure';
import { Notice } from '../../ui';
import { useAddonCatalogue, useAttachAddon, useChangeAddon, useOrganizationAddons } from './addons-queries';

/** The add-on head office attaches from this page. The catalogue's other keys arrive with their own features. */
const BOOT = 'boot';

/** The PC counts the api takes (`ADDON_MAX_QUANTITY` in the contract). */
const MAX_PCS = 10_000;

/** A whole number of PCs from 1 to 10,000, or `null`. */
function pcsOf(text: string): number | null {
  const trimmed = text.trim();
  if (!/^[0-9]+$/.test(trimmed)) return null;
  const value = Number(trimmed);
  return Number.isSafeInteger(value) && value >= 1 && value <= MAX_PCS ? value : null;
}

export function AddonsPanel({ organizationId, currency }: { readonly organizationId: string; readonly currency: string }): ReactNode {
  const { t } = useTranslation('hq');
  const addons = useOrganizationAddons(organizationId);
  const catalogue = useAddonCatalogue();
  const boot = catalogue.data?.data.find((addon) => addon.key === BOOT);

  let body: ReactNode;
  if (addons.data === undefined) {
    body = addons.isError ? (
      <LoadFailure error={addons.error} message={t('organizations.addons.unavailable')} onRetry={() => void addons.refetch()} />
    ) : (
      <Loading />
    );
  } else {
    const active = addons.data.data.filter((addon) => addon.status === 'active');
    const ended = addons.data.data.filter((addon) => addon.status !== 'active');
    const bootActive = active.some((addon) => addon.addonKey === BOOT);
    body = (
      <div className="console-hq-payments">
        {active.map((addon) => (
          <ActiveAddon key={addon.id} organizationId={organizationId} addon={addon} />
        ))}
        {bootActive || catalogue.data === undefined ? null : <AttachBlock organizationId={organizationId} currency={currency} addon={boot} />}
        <TotalBlock total={addons.data.monthlyTotal} />
        {ended.length === 0 ? null : <EndedBlock addons={ended} />}
      </div>
    );
  }

  return (
    <section className="console-hq-panel" aria-labelledby="console-hq-addons">
      <h2 id="console-hq-addons" className="console-hq-owner__title">
        <span>{t('organizations.addons.title')}</span>
        <InfoTip>{t('organizations.addons.intro')}</InfoTip>
      </h2>
      {body}
    </section>
  );
}

/** One active add-on: its PCs, price per PC and monthly charge, with the ways to change it. */
function ActiveAddon({ organizationId, addon }: { readonly organizationId: string; readonly addon: OrganizationAddon }) {
  const { t } = useTranslation('hq');
  const format = useVenueFormatter();
  const [changing, setChanging] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  return (
    <div className="console-hq-payments__block">
      <h3>{addon.name}</h3>
      <p>
        <Badge tone="ok" dot>
          {t('organizations.addons.status.active')}
        </Badge>
      </p>
      <dl className="console-hq-terms">
        <dt>{t('organizations.addons.licensed')}</dt>
        <dd className="clav-figure">{t('organizations.addons.pcs', { count: addon.quantity })}</dd>
        <dt>{t('organizations.addons.unitPrice')}</dt>
        <dd className="clav-figure">{t('organizations.addons.unitPriceValue', { price: format.money(addon.unitPrice) })}</dd>
        <dt>{t('organizations.addons.monthlyCharge')}</dt>
        <dd className="clav-figure">{format.money(addon.monthlyCharge)}</dd>
        <dt>{t('organizations.addons.since')}</dt>
        <dd>{format.date(addon.startedAt, 'medium')}</dd>
      </dl>
      <div className="console-form__actions">
        <Button variant="secondary" onClick={() => setChanging(true)}>
          {t('organizations.addons.changePcs')}
        </Button>
        <Button variant="secondary" onClick={() => setCancelling(true)}>
          {t('organizations.addons.cancelAddon')}
        </Button>
      </div>
      <Dialog
        open={changing}
        onOpenChange={setChanging}
        size="sm"
        title={t('organizations.addons.dialog.changeTitle')}
        description={t('organizations.addons.dialog.changeIntro')}
      >
        {changing ? <ChangePcsForm organizationId={organizationId} addon={addon} onClose={() => setChanging(false)} /> : null}
      </Dialog>
      <CancelDialog organizationId={organizationId} addon={addon} open={cancelling} onClose={() => setCancelling(false)} />
    </div>
  );
}

/** Clavian Boot is not attached: why it cannot be yet, or the way to attach it. */
function AttachBlock({ organizationId, currency, addon }: { readonly organizationId: string; readonly currency: string; readonly addon: Addon | undefined }) {
  const { t } = useTranslation('hq');
  const [open, setOpen] = useState(false);
  // The catalogue's name once it has one; before that, the product's.
  const name = addon?.name ?? t('organizations.addons.bootName');
  let blocked: string | undefined;
  if (addon === undefined) blocked = t('organizations.addons.noPrice', { name });
  else if (!addon.active) blocked = t('organizations.addons.notOffered', { name });
  else if (addon.price.currency !== currency) blocked = t('organizations.addons.currencyDiffers', { name, priced: addon.price.currency, currency });

  return (
    <div className="console-hq-payments__block">
      <h3>{name}</h3>
      <p>{t('organizations.addons.notAttached', { name })}</p>
      {blocked === undefined && addon !== undefined ? (
        <>
          <div className="console-form__actions">
            <Button variant="primary" onClick={() => setOpen(true)}>
              {t('organizations.addons.attach', { name })}
            </Button>
          </div>
          <Dialog
            open={open}
            onOpenChange={setOpen}
            size="sm"
            title={t('organizations.addons.dialog.attachTitle', { name })}
            description={t('organizations.addons.dialog.attachIntro')}
          >
            {open ? <AttachForm organizationId={organizationId} addon={addon} onClose={() => setOpen(false)} /> : null}
          </Dialog>
        </>
      ) : (
        <Notice tone="info">{blocked}</Notice>
      )}
    </div>
  );
}

function TotalBlock({ total }: { readonly total: OrganizationAddon['monthlyCharge'] }) {
  const { t } = useTranslation('hq');
  const format = useVenueFormatter();
  return (
    <div className="console-hq-payments__block">
      <h3>{t('organizations.addons.total')}</h3>
      <p>
        <strong className="clav-figure">{format.money(total)}</strong>
      </p>
    </div>
  );
}

function EndedBlock({ addons }: { readonly addons: readonly OrganizationAddon[] }) {
  const { t } = useTranslation('hq');
  const format = useVenueFormatter();
  return (
    <div className="console-hq-payments__block">
      <h3>{t('organizations.addons.ended')}</h3>
      <ul className="console-muted">
        {addons.map((addon) => (
          <li key={addon.id}>
            {t('organizations.addons.endedRow', {
              name: addon.name,
              pcs: t('organizations.addons.pcs', { count: addon.quantity }),
              from: format.date(addon.startedAt, 'medium'),
              to: addon.endedAt === null ? '' : format.date(addon.endedAt, 'medium'),
            })}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The monthly charge a count would make, at a price per PC, as the server charges it. */
function Preview({ unitPrice, pcs }: { readonly unitPrice: Addon['price']; readonly pcs: number | null }) {
  const { t } = useTranslation('hq');
  const format = useVenueFormatter();
  if (pcs === null) return null;
  return (
    <p className="console-muted clav-figure" role="status">
      {t('organizations.addons.dialog.preview', {
        price: format.money(unitPrice),
        pcs: t('organizations.addons.pcs', { count: pcs }),
        total: format.money(addonMonthlyCharge(unitPrice, pcs)),
      })}
    </p>
  );
}

/** A failure the api explains (a conflict, a currency it refused) in its words, anything else in ours. */
function failureText(error: unknown, fallback: string): string {
  const status = problemStatus(error);
  const detail = problemDetail(error);
  return (status === 409 || status === 422 || status === 404) && detail !== undefined ? detail : fallback;
}

function AttachForm({ organizationId, addon, onClose }: { readonly organizationId: string; readonly addon: Addon; readonly onClose: () => void }) {
  const { t } = useTranslation('hq');
  const { show } = useToast();
  const attach = useAttachAddon(organizationId);
  const [text, setText] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const [failure, setFailure] = useState<string | undefined>(undefined);
  const pcs = pcsOf(text);

  function submit(event: FormEvent): void {
    event.preventDefault();
    setFailure(undefined);
    if (pcs === null) {
      setError(t('organizations.addons.dialog.quantityInvalid'));
      return;
    }
    setError(undefined);
    attach.mutate(
      { addonKey: addon.key, quantity: pcs },
      {
        onSuccess: () => {
          onClose();
          show({ tone: 'ok', title: t('organizations.addons.dialog.attached', { name: addon.name }) });
        },
        onError: (caught) => setFailure(failureText(caught, t('organizations.addons.dialog.failed'))),
      },
    );
  }

  return (
    <form className="console-form" noValidate aria-label={t('organizations.addons.dialog.attachTitle', { name: addon.name })} onSubmit={submit}>
      {failure === undefined ? null : (
        <Notice tone="danger" role="alert">
          {failure}
        </Notice>
      )}
      <TextField
        label={t('organizations.addons.dialog.quantity')}
        description={t('organizations.addons.dialog.quantityHint')}
        required
        inputMode="numeric"
        autoComplete="off"
        value={text}
        error={error}
        onChange={(event) => setText(event.target.value)}
      />
      <Preview unitPrice={addon.price} pcs={pcs} />
      <div className="console-form__actions">
        <Button variant="ghost" onClick={onClose}>
          {t('organizations.addons.dialog.cancel')}
        </Button>
        <Button type="submit" variant="primary" busy={attach.isPending}>
          {t('organizations.addons.dialog.attach')}
        </Button>
      </div>
    </form>
  );
}

function ChangePcsForm({ organizationId, addon, onClose }: { readonly organizationId: string; readonly addon: OrganizationAddon; readonly onClose: () => void }) {
  const { t } = useTranslation('hq');
  const { show } = useToast();
  const change = useChangeAddon(organizationId);
  const [text, setText] = useState(String(addon.quantity));
  const [error, setError] = useState<string | undefined>(undefined);
  const [failure, setFailure] = useState<string | undefined>(undefined);
  const pcs = pcsOf(text);

  function submit(event: FormEvent): void {
    event.preventDefault();
    setFailure(undefined);
    if (pcs === null) {
      setError(t('organizations.addons.dialog.quantityInvalid'));
      return;
    }
    setError(undefined);
    change.mutate(
      { organizationAddonId: addon.id, body: { quantity: pcs } },
      {
        onSuccess: () => {
          onClose();
          show({ tone: 'ok', title: t('organizations.addons.dialog.saved') });
        },
        onError: (caught) => setFailure(failureText(caught, t('organizations.addons.dialog.failed'))),
      },
    );
  }

  return (
    <form className="console-form" noValidate aria-label={t('organizations.addons.dialog.changeTitle')} onSubmit={submit}>
      {failure === undefined ? null : (
        <Notice tone="danger" role="alert">
          {failure}
        </Notice>
      )}
      <TextField
        label={t('organizations.addons.dialog.quantity')}
        description={t('organizations.addons.dialog.quantityHint')}
        required
        inputMode="numeric"
        autoComplete="off"
        value={text}
        error={error}
        onChange={(event) => setText(event.target.value)}
      />
      <Preview unitPrice={addon.unitPrice} pcs={pcs} />
      <div className="console-form__actions">
        <Button variant="ghost" onClick={onClose}>
          {t('organizations.addons.dialog.cancel')}
        </Button>
        <Button type="submit" variant="primary" busy={change.isPending}>
          {t('organizations.addons.dialog.save')}
        </Button>
      </div>
    </form>
  );
}

function CancelDialog({
  organizationId,
  addon,
  open,
  onClose,
}: {
  readonly organizationId: string;
  readonly addon: OrganizationAddon;
  readonly open: boolean;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation('hq');
  const { show } = useToast();
  const change = useChangeAddon(organizationId);
  const [failure, setFailure] = useState<string | undefined>(undefined);

  function confirm(): void {
    setFailure(undefined);
    change.mutate(
      { organizationAddonId: addon.id, body: { status: 'cancelled' } },
      {
        onSuccess: () => {
          onClose();
          show({ tone: 'ok', title: t('organizations.addons.cancelDialog.cancelled', { name: addon.name }) });
        },
        onError: (caught) => setFailure(failureText(caught, t('organizations.addons.cancelDialog.failed'))),
      },
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => (next ? undefined : onClose())}
      size="sm"
      title={t('organizations.addons.cancelDialog.title', { name: addon.name })}
      description={t('organizations.addons.cancelDialog.body')}
    >
      <div className="console-form">
        {failure === undefined ? null : (
          <Notice tone="danger" role="alert">
            {failure}
          </Notice>
        )}
        <div className="console-form__actions">
          <Button variant="ghost" onClick={onClose}>
            {t('organizations.addons.cancelDialog.keep')}
          </Button>
          <Button variant="danger" busy={change.isPending} onClick={confirm}>
            {t('organizations.addons.cancelDialog.confirm')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
